import { formatCopy } from "../../theme/copy";
import { Fragment } from "react";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupHeading } from "../visualGroups";
import { GridRowGroups } from "../grid/GroupHeadings";
import { Box, type SxProps } from "@mui/material";
import { ActionButton } from "../../components/ui";
import { comparisonImageStyle } from "../../components/ui/CommonImageComp/styles";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import {
  compactGridButtonStyle,
  compactGridCardStyle,
  compactGridListStyle,
  wideGridInstrumentStyle,
  wideGridTableStyle,
} from "../grid/gridInstrumentStyle";
import type {
  GridThisOrThatDefinition,
  GridThisOrThatDraft,
  GridThisOrThatEvent,
} from "./core";
import {
  gridThisOrThatRowStyle,
  questionWrapper,
  textStyle,
} from "./styles";

type GridThisOrThatViewProps = QuestionViewProps<
  GridThisOrThatDefinition,
  GridThisOrThatDraft,
  GridThisOrThatEvent
>;

export const GridThisOrThatView = (props: GridThisOrThatViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const compact = props.display.arrangement === "compact";
  const groupedWide = !compact && Boolean(props.definition.rowGroups?.length);

  const pairRow = (
    pair: GridThisOrThatDefinition["pairs"][number],
    rowIndex: number,
  ) => {
    return (
      <Box
        sx={{
          ...(compact ? compactGridCardStyle(theme) : {}),
          ...gridThisOrThatRowStyle(
            theme,
            compact,
            rowIndex === props.definition.pairs.length - 1,
          ),
          display: "grid",
          gridTemplateColumns:
            !compact && props.definition.dontKnow
              ? "minmax(0, 1fr) auto minmax(0, 1fr) auto"
              : "minmax(0, 1fr) auto minmax(0, 1fr)",
          minHeight: compact
            ? undefined
            : pair.some((option) => option.image)
              ? 110
              : 0,
          gap:
            compact || !theme.show_grid_lines
              ? `${theme.tokens.spacing["2"]}px`
              : undefined,
          alignItems: "stretch",
        }}
      >
        {pair.map((option, optionIndex) => {
          const selected = props.draft.selectedByRow[rowIndex] === option.value;
          const disabled = option.disabled || !props.controls.canSubmit;
          return (
            <Box
              key={option.value}
              sx={{
                gridColumn: optionIndex === 0 ? 1 : 3,
                gridRow: 1,
                ...(!compact && theme.show_grid_lines && optionIndex > 0
                  ? {
                      borderLeft: `1px solid ${theme.tokens.color.action.primary}`,
                    }
                  : {}),
              }}
            >
              <ActionButton
                ariaLabel={formatCopy(theme.copy.labels.rowOption, {
                  row: rowIndex + 1,
                  option: option.label,
                })}
                ariaPressed={selected}
                isSelected={selected}
                disabled={disabled}
                onClick={() =>
                  props.dispatch({
                    type: "select",
                    rowIndex,
                    value: option.value,
                  })
                }
                testId={`LeftRight-${rowIndex + 1}-${optionIndex + 1}`}
                currentVal={{
                  image: option.image,
                  text: option.label,
                  display_text: option.displayLabel,
                  alt_text: option.label,
                }}
                buttonImageStyle={comparisonImageStyle}
                wrapperSx={{ width: "100%", height: "100%" }}
                style={{
                  ...compactGridButtonStyle(theme, selected),
                  minHeight: compact ? 80 : theme.tokens.spacing["6"] * 2,
                  ...(!compact && !groupedWide && theme.show_grid_lines
                    ? { border: 0, borderRadius: 0 }
                    : {}),
                }}
              />
            </Box>
          );
        })}
        <Box
          sx={
            [
              textStyle(theme),
              {
                gridColumn: 2,
                gridRow: 1,
                alignSelf: "stretch",
                display: "flex",
                alignItems: "center",
                ...(!compact && theme.show_grid_lines
                  ? {
                      marginX: 0,
                      paddingX: `${theme.tokens.spacing["2"]}px`,
                      borderLeft: `1px solid ${theme.tokens.color.action.primary}`,
                    }
                  : {}),
              },
            ] as SxProps
          }
        >
          {theme.copy.labels.or}
        </Box>
        {props.definition.dontKnow && (
          <Box
            sx={{
              gridColumn: compact ? "1 / -1" : 4,
              gridRow: compact ? 2 : 1,
              justifySelf: compact ? "end" : "stretch",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(!compact && theme.show_grid_lines
                ? {
                    padding: `${theme.tokens.spacing["2"]}px`,
                    borderLeft: `1px solid ${theme.tokens.color.action.primary}`,
                  }
                : {}),
            }}
          >
            <ActionButton
              currentVal={{
                text: props.definition.dontKnow.label,
                display_text: true,
              }}
              isSelected={
                props.draft.selectedByRow[rowIndex] ===
                props.definition.dontKnow.value
              }
              ariaPressed={
                props.draft.selectedByRow[rowIndex] ===
                props.definition.dontKnow.value
              }
              ariaLabel={formatCopy(theme.copy.labels.rowOption, {
                row: rowIndex + 1,
                option: props.definition.dontKnow.label,
              })}
              ariaDisabled={!props.controls.canSubmit}
              onClick={() => props.dispatch({ type: "dont-know", rowIndex })}
              testId={`grid-this-or-that-dont-know-${rowIndex}`}
            />
          </Box>
        )}
      </Box>
    );
  };
  return (
    <Box
      data-testid="grid-this-or-that-view"
      sx={
        compact
          ? compactGridListStyle(theme)
          : groupedWide
            ? wideGridInstrumentStyle(theme, theme.show_grid_lines, true)
            : ([
                questionWrapper(theme),
                { width: "100%", maxWidth: "100%" },
              ] as SxProps)
      }
    >
      {groupedWide ? (
        <Box component="table" sx={wideGridTableStyle}>
          <GridRowGroups
            rows={props.definition.pairs}
            groups={props.definition.rowGroups}
            rowId={(_, index) => props.definition.rowIds?.[index]}
            columns={1}
          >
            {(pair, rowIndex) => (
              <Box component="tr">
                <Box
                  component="td"
                  sx={{ padding: `${theme.tokens.spacing["2"]}px` }}
                >
                  {pairRow(pair, rowIndex)}
                </Box>
              </Box>
            )}
          </GridRowGroups>
        </Box>
      ) : (
        props.definition.pairs.map((pair, rowIndex) => (
          <Fragment key={rowIndex}>
            {compact && (
              <VisualGroupHeading
                text={groupHeading(
                  props.definition.rowGroups,
                  props.definition.rowIds?.[rowIndex],
                )}
              />
            )}
            {pairRow(pair, rowIndex)}
          </Fragment>
        ))
      )}
    </Box>
  );
};
